import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { competitors, getRouteMetadata } from '../utils/seoMetadata.js';
import './Blog.css';
import './AlternativePage.css';

const comparisonRows = [
  ['Collect text testimonials', 'Included', 'Check current plan'],
  ['Collect video testimonials', 'Included', 'Check current plan'],
  ['Collect audio testimonials', 'Included', 'Check current plan'],
  ['Manage customer stories', 'Included', 'Check current plan'],
  ['Publish website widgets', 'Included', 'Check current plan'],
  ['Branded collection workflow', 'Included', 'Check current plan'],
  ['Review before publishing', 'Included', 'Check current plan']
];

const collectionChannels = ['Email campaigns', 'Messaging apps', 'Customer support', 'Sales follow-ups', 'QR codes', 'Post-purchase messages'];
const publishingChannels = ['Homepage', 'Product and service pages', 'Landing pages', 'Pricing pages', 'Sales presentations', 'Social media', 'Email and advertising campaigns'];
const workflowSteps = ['Collect', 'Review', 'Organize', 'Publish', 'Reuse'];

const alternativeProfiles = {
  trustpilot: {
    focus: 'owning first-party customer stories instead of relying only on a public review marketplace',
    audience: 'Teams that want more control over how customer feedback is collected, approved and reused',
    bestFor: 'Brands that want to complement public ratings with detailed, permission-based customer stories on channels they control.',
    switching: 'Keep useful public reviews in place while building a separate first-party testimonial library. Invite loyal customers to expand on the result they achieved, then publish approved stories on high-intent pages.',
    campaigns: ['Turn short reviews into detailed customer spotlights', 'Match stories to products, services or buyer concerns', 'Use approved quotes in conversion and retention campaigns'],
    images: ['photo-1556761175-b413da4baf72', 'photo-1556761175-5973dc0f32e7', 'photo-1551836022-d5d88e9218df']
  },
  boastio: {
    focus: 'building a straightforward text, audio and video testimonial workflow',
    audience: 'Growing businesses that want one repeatable process from request to publication',
    bestFor: 'Small and growing teams that want flexible testimonial formats with a clear review and publishing routine.',
    switching: 'Audit existing Boast.io submissions, confirm publishing consent and group the strongest stories by service or audience. Recreate the request with fewer, more focused prompts before inviting new responses.',
    campaigns: ['Collect post-purchase stories with one reusable link', 'Build separate proof libraries for each service line', 'Refresh landing pages with recent customer outcomes'],
    images: ['photo-1521737711867-e3b97375f902', 'photo-1552664730-d307ca884978', 'photo-1524504388940-b1c1722653e1']
  },
  bonjoro: {
    focus: 'collecting reusable customer proof alongside personal customer communication',
    audience: 'Customer success and marketing teams turning relationships into credible stories',
    bestFor: 'Relationship-led teams that want to move from personal customer messages to approved, reusable marketing proof.',
    switching: 'Identify the customer moments that already trigger a personal follow-up, such as onboarding completion or a success milestone. Add a focused testimonial request at those moments and route responses into a shared review process.',
    campaigns: ['Ask for feedback after customer success milestones', 'Convert personal praise into permissioned testimonials', 'Equip account teams with industry-specific customer stories'],
    images: ['photo-1556761175-4b46a572b786', 'photo-1522071820081-009f0129c71c', 'photo-1543269865-cbf427effbad']
  },
  senja: {
    focus: 'keeping testimonial collection, approval and multi-channel publishing in one workspace',
    audience: 'Lean marketing teams that need an organized library of customer proof',
    bestFor: 'Lean teams that care about a searchable proof library and a consistent process for reviewing new submissions.',
    switching: 'Export or catalogue your approved Senja stories before changing workflows. Preserve customer attribution and consent, then tag each imported story by audience, product and outcome so marketers can find it quickly.',
    campaigns: ['Create proof collections for individual buyer personas', 'Pair customer quotes with measurable outcomes', 'Keep fresh testimonials ready for product launches'],
    images: ['photo-1524758631624-e2822e304c36', 'photo-1556761175-129418cb2dfe', 'photo-1556761175-4b46a572b786']
  },
  famewall: {
    focus: 'turning customer feedback into polished social proof for key buying pages',
    audience: 'Founders and marketers who want to publish their strongest stories quickly',
    bestFor: 'Founders and marketers who want to collect new stories as deliberately as they display existing praise.',
    switching: 'List every page where a Famewall currently appears and note the message each wall supports. Migrate approved proof first, then create targeted requests to fill gaps in industries, use cases or outcomes.',
    campaigns: ['Build focused testimonial walls for campaign pages', 'Collect stories that address common sales objections', 'Reuse standout quotes across social and email'],
    images: ['photo-1522202176988-66273c2fd55f', 'photo-1531497865144-0464ef8fb9a9', 'photo-1529156069898-49953e39b3ac']
  },
  'testimonial-hero': {
    focus: 'creating an ongoing in-house testimonial program with flexible response formats',
    audience: 'Teams that prefer continuous collection over a project-based production model',
    bestFor: 'Teams that want an always-on customer story program rather than coordinating each testimonial as a separate production project.',
    switching: 'Separate finished creative assets from raw customer permissions and source responses. Bring approved assets into your library, then automate lightweight requests for future stories around important lifecycle moments.',
    campaigns: ['Maintain an ongoing pipeline of customer stories', 'Collect quick video responses without a production day', 'Create proof for multiple products and market segments'],
    images: ['photo-1542744173-8e7e53415bb0', 'photo-1551836022-d5d88e9218df', 'photo-1497366811353-6870744d04b2']
  },
  testimonialto: {
    focus: 'collecting and organizing text, audio and video stories through a branded request',
    audience: 'SaaS, service and creator businesses building an accessible proof library',
    bestFor: 'SaaS, service and creator businesses that need a branded collection experience and reusable website proof.',
    switching: 'Map current Testimonial.to forms, walls and embeds before moving content. Carry over attribution and permissions, simplify duplicate forms and rebuild requests around the questions that produced the strongest answers.',
    campaigns: ['Collect launch feedback from early customers', 'Create a wall of love for high-intent visitors', 'Give sales teams proof grouped by use case'],
    images: ['photo-1517245386807-bb43f82c33c4', 'photo-1556761175-5973dc0f32e7', 'photo-1556761175-b413da4baf72']
  },
  videoask: {
    focus: 'moving from conversational video forms to a dedicated testimonial workflow',
    audience: 'Teams that need customer stories ready for review, organization and publishing',
    bestFor: 'Teams whose priority is an organized testimonial program rather than a broader conversational video funnel.',
    switching: 'Document which VideoAsk flows collect leads, support customers or request testimonials. Move only the testimonial use case first, preserving successful prompts while shortening the path from invitation to response.',
    campaigns: ['Request a story after onboarding or renewal', 'Collect video proof for product and service pages', 'Offer text or audio when video is not convenient'],
    images: ['photo-1531482615713-2afd69097998', 'photo-1553877522-43269d4ea984', 'photo-1535223289827-42f1e9919769']
  },
  'vocal-video': {
    focus: 'giving customers a lightweight way to submit video, audio or written experiences',
    audience: 'Marketing teams that want flexible collection without a production-heavy process',
    bestFor: 'Marketing teams that want flexible response formats and direct control of the customer proof they publish.',
    switching: 'Inventory finished Vocal Video assets, source recordings and customer permissions separately. Preserve final videos where useful, then use a simpler ongoing request to collect new text, audio and video responses.',
    campaigns: ['Capture authentic remote customer interviews', 'Collect written alternatives to video responses', 'Repurpose approved stories for web, sales and social'],
    images: ['photo-1551836022-4c4c79ecde51', 'photo-1573496799652-408c2ac9fe98', 'photo-1524250502761-1ac6f2e30d43']
  },
  endorsal: {
    focus: 'combining direct testimonial requests with an organized publishing workflow',
    audience: 'Businesses that want to curate first-party proof for their own website and campaigns',
    bestFor: 'Businesses that want a direct collection workflow and more ways for customers to tell a complete story.',
    switching: 'Review current Endorsal forms, widgets and imported reviews before replacing anything. Keep the strongest approved proof, remove stale or duplicate entries and build new requests around specific customer outcomes.',
    campaigns: ['Collect richer stories than a star rating alone', 'Organize proof by product, location or service', 'Publish curated testimonials at key decision points'],
    images: ['photo-1556761175-b413da4baf72', 'photo-1551836022-8b2858c9c69b', 'photo-1516321318423-f06f85e504b3']
  },
  vouch: {
    focus: 'capturing authentic customer responses and making them reusable across marketing',
    audience: 'B2B teams that want customer voices available to sales, web and campaign teams',
    bestFor: 'B2B marketing teams that want text, audio and video proof available across web, sales and demand generation.',
    switching: 'Catalogue Vouch recordings by account, persona and use case before migration. Retain customer permissions, identify coverage gaps and invite strategic customers to answer concise prompts tied to measurable business outcomes.',
    campaigns: ['Build account-specific proof for sales conversations', 'Collect customer voices for demand-generation campaigns', 'Create a searchable library by industry and outcome'],
    images: ['photo-1524178232363-1fb2b075b655', 'photo-1543269865-cbf427effbad', 'photo-1521737711867-e3b97375f902']
  }
};

function getAlternative(pathname) {
  if (pathname === '/trustpilot-alternatives' || pathname === '/trustpilot-alternatives/') {
    return { name: 'Trustpilot', slug: 'trustpilot' };
  }

  const slug = pathname.split('/').filter(Boolean).at(-1);
  return competitors.find((competitor) => competitor.slug === slug);
}

function AlternativeSections({ alternative, faqs }) {
  const profile = alternativeProfiles[alternative.slug] || alternativeProfiles.boastio;
  const imageUrl = (photo) => `/images/alternatives/${photo}.webp`;

  return (
    <>
      <section className="alternative-section">
        <div className="container alternative-content alternative-split">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Looking for a better way?</p>
            <h2>Give your customers an easier way to speak</h2>
            <p>Clientalio is a practical {alternative.name} alternative for teams focused on {profile.focus}. Send one simple link and let each customer respond with text, video or audio in the format that feels natural.</p>
            <p>Customers are more likely to share useful feedback when the process is quick, clear and works on the device already in their hand.</p>
          </div>
          <figure className="alternative-image">
            <img src={imageUrl(profile.images[0])} alt={`${alternative.name} alternative for teams collecting customer stories`} loading="lazy" />
            <figcaption>{profile.audience}.</figcaption>
          </figure>
        </div>
        <div className="container alternative-content">
          <div className="alternative-benefits">
            <article>
              <h3>Written testimonials</h3>
              <p>Ideal for product pages, landing pages, proposals and sales materials.</p>
            </article>
            <article>
              <h3>Video testimonials</h3>
              <p>Let customers explain their experience in their own voice and create a more human connection.</p>
            </article>
            <article>
              <h3>Audio testimonials</h3>
              <p>Offer a simple option for customers who want to talk without appearing on camera.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="alternative-section alternative-section-muted">
        <div className="container alternative-content">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Collect from anywhere</p>
            <h2>One request can start a new customer story</h2>
            <p>Your customers may use different devices and communication channels. Put the same simple testimonial request wherever the conversation already happens.</p>
          </div>
          <div className="alternative-chip-list">
            {collectionChannels.map((channel) => <span key={channel}>{channel}</span>)}
          </div>
        </div>
      </section>

      <section className="alternative-section">
        <div className="container alternative-content alternative-split">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Where Clientalio fits</p>
            <h2>Is Clientalio the right {alternative.name} alternative for you?</h2>
            <p>{profile.bestFor}</p>
            <p>The strongest testimonial system should fit both sides of the workflow: it must be easy for a customer to respond and practical for your team to turn that response into useful, approved content.</p>
            <h3>Campaigns you can build</h3>
            <ul className="alternative-check-list">
              {profile.campaigns.map((campaign) => <li key={campaign}>{campaign}</li>)}
            </ul>
          </div>
          <figure className="alternative-image alternative-image-tall">
            <img src={imageUrl(profile.images[2])} alt={`Team planning customer proof while comparing Clientalio and ${alternative.name}`} loading="lazy" />
            <figcaption>Build a proof library around the questions your future buyers ask.</figcaption>
          </figure>
        </div>
      </section>

      <section className="alternative-section alternative-section-muted">
        <div className="container alternative-content">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Migration guidance</p>
            <h2>Move from {alternative.name} without losing valuable proof</h2>
            <p>{profile.switching}</p>
          </div>
          <div className="alternative-detail-grid">
            <article>
              <h3>Inventory</h3>
              <p>Record the testimonial, customer name, company, source format, publication status and every page where the story currently appears.</p>
            </article>
            <article>
              <h3>Permission</h3>
              <p>Confirm that consent covers the channels and formats you plan to use. Keep the original response and approval history together.</p>
            </article>
            <article>
              <h3>Prioritize</h3>
              <p>Move current, specific stories first. Give priority to proof that answers buyer objections or demonstrates a clear customer outcome.</p>
            </article>
            <article>
              <h3>Refresh</h3>
              <p>Contact strong advocates when an older quote lacks context. A focused follow-up can turn brief praise into a complete customer story.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="alternative-section">
        <div className="container alternative-content alternative-split alternative-split-reverse">
          <figure className="alternative-image">
            <img src={imageUrl(profile.images[1])} alt={`Organizing testimonials while evaluating ${alternative.name}`} loading="lazy" />
          </figure>
          <div className="alternative-intro">
            <p className="blog-eyebrow">Keep every story under control</p>
            <h2>Manage customer testimonials in one place</h2>
            <p>Good testimonials get lost when they live across inboxes, spreadsheets, forms and folders. Clientalio gives your team one place to review new submissions, choose the strongest stories and prepare approved content for publishing.</p>
            <ul className="alternative-check-list">
              <li>Collect and review new responses</li>
              <li>Approve testimonials before publishing</li>
              <li>Organize and find your strongest social proof</li>
              <li>Keep reusable feedback accessible to your team</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="alternative-section alternative-section-muted">
        <div className="container alternative-content">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Turn feedback into social proof</p>
            <h2>Ask questions that uncover a useful story</h2>
            <p>A star rating is helpful, but a specific customer experience can show future buyers why people choose your business and what they can expect.</p>
          </div>
          <div className="alternative-question-grid">
            <blockquote>“Why did you choose us?”</blockquote>
            <blockquote>“What problem did we help you solve?”</blockquote>
            <blockquote>“What changed after using our product or service?”</blockquote>
            <blockquote>“What would you tell someone considering us?”</blockquote>
          </div>
        </div>
      </section>

      <section className="alternative-section">
        <div className="container alternative-content">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Feature overview</p>
            <h2>Clientalio vs {alternative.name}</h2>
            <p>Choose based on the workflow your team needs. Product capabilities and plans change, so confirm the current {alternative.name} offering before making a final decision.</p>
          </div>
          <div className="alternative-table-wrap">
            <table className="alternative-table">
              <thead><tr><th>Capability</th><th>Clientalio</th><th>{alternative.name}</th></tr></thead>
              <tbody>
                {comparisonRows.map(([feature, clientalio, competitor]) => (
                  <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="alternative-section alternative-section-muted">
        <div className="container alternative-content alternative-decision">
          <div>
            <p className="blog-eyebrow">Share stories where they matter</p>
            <h2>Put your best testimonials in front of buyers</h2>
            <p>Collecting a testimonial is only the first step. Publish approved stories where potential customers are comparing options and deciding whether to trust your business.</p>
          </div>
          <ul>{publishingChannels.map((channel) => <li key={channel}>{channel}</li>)}</ul>
        </div>
      </section>

      <section className="alternative-section">
        <div className="container alternative-content">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Choose for the work ahead</p>
            <h2>What to consider before switching from {alternative.name}</h2>
            <p>A useful comparison goes beyond a feature checklist. Start with the way your customers respond, the people who manage submissions and the places where approved proof needs to appear.</p>
          </div>
          <div className="alternative-detail-grid">
            <article>
              <h3>Customer experience</h3>
              <p>Check how many steps a customer must complete, whether the request works well on mobile and whether text, audio and video are all available.</p>
            </article>
            <article>
              <h3>Review and consent</h3>
              <p>Define who reviews each response, how publishing approval is recorded and what your team does when a customer asks for an update.</p>
            </article>
            <article>
              <h3>Organization</h3>
              <p>Look for a workflow that helps teams group stories by product, audience, campaign or use case instead of searching through disconnected files.</p>
            </article>
            <article>
              <h3>Distribution</h3>
              <p>Plan how proof will move from collection into website pages, sales material, social posts, email campaigns and paid advertising.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="alternative-section alternative-section-muted">
        <div className="container alternative-content alternative-split">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Plan your transition</p>
            <h2>Build a testimonial system your team will use</h2>
            <p>Moving from {alternative.name} is an opportunity to clean up old feedback and establish a repeatable process. Decide what should be imported, who owns new requests and which channels need fresh proof first.</p>
            <ul className="alternative-check-list">
              <li>Audit existing testimonials and confirm customer permissions</li>
              <li>Group approved stories by product, industry and buyer concern</li>
              <li>Create a short request with three or four focused questions</li>
              <li>Assign review, approval and publishing responsibilities</li>
              <li>Measure response rates and where testimonials influence buyers</li>
            </ul>
          </div>
          <div className="alternative-stat-panel">
            <span>One customer story</span>
            <strong>Many useful formats</strong>
            <p>Turn a detailed response into a website quote, a video clip, a sales slide, an email story and a social post while keeping the original experience intact.</p>
          </div>
        </div>
      </section>

      <section className="alternative-section">
        <div className="container alternative-content">
          <div className="alternative-intro">
            <p className="blog-eyebrow">A straightforward workflow</p>
            <h2>Turn happy customers into marketing content</h2>
            <p>Clientalio is built around a clear process that helps your team turn first-hand customer experiences into a reusable library of trusted marketing assets.</p>
          </div>
          <ol className="alternative-workflow">
            {workflowSteps.map((step, index) => <li key={step}><span>{index + 1}</span>{step}</li>)}
          </ol>
          <div className="alternative-prose">
            <h3>Why choose Clientalio as a {alternative.name} alternative?</h3>
            <p>Public review and testimonial platforms can serve different goals. Clientalio is designed for businesses that want a direct relationship with their customer stories: request feedback, support multiple response formats, review submissions and control how approved testimonials appear across their own marketing.</p>
            <p>Use those stories to build trust with new buyers, support sales conversations, strengthen landing pages and make advertising messages more credible. Instead of leaving valuable feedback unused, your team can keep it ready for the moment a buyer needs reassurance.</p>
          </div>
        </div>
      </section>

      <section className="alternative-section alternative-section-muted">
        <div className="container alternative-content">
          <div className="alternative-intro">
            <p className="blog-eyebrow">Frequently asked questions</p>
            <h2>{alternative.name} alternative FAQs</h2>
          </div>
          <div className="alternative-faqs">
            {faqs.map((faq) => (
              <details key={faq.question}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="alternative-section">
        <div className="container alternative-content alternative-final-cta">
          <p className="blog-eyebrow">Collect feedback. Build trust.</p>
          <h2>Start collecting authentic customer stories</h2>
          <p>Create your Clientalio testimonial page, send your first request and turn customer experiences into marketing assets.</p>
          <Link className="alternative-primary-action" to="/signup">Start collecting testimonials</Link>
        </div>
      </section>
    </>
  );
}

function AlternativePage() {
  const renderLegacyContent = false;
  const location = useLocation();
  const metadata = getRouteMetadata(location.pathname);
  const alternative = getAlternative(location.pathname);

  if (!alternative) {
    return (
      <main className="blog-page">
        <section className="blog-content">
          <div className="container blog-content-inner">
            <h1>Alternative not found</h1>
            <Link className="blog-read-link" to="/alternatives">View all alternatives</Link>
          </div>
        </section>
      </main>
    );
  }

  const faqs = [
    {
      question: `What is a good ${alternative.name} alternative?`,
      answer: `Clientalio is an option for teams that want to collect, manage, and showcase text and video customer testimonials in one workflow.`
    },
    {
      question: `Can Clientalio collect video testimonials?`,
      answer: 'Yes. Clientalio supports video, text, and audio testimonial collection through customer-friendly requests.'
    },
    {
      question: `Can I publish testimonials on my website?`,
      answer: 'Yes. Clientalio provides website-ready testimonial widgets for turning approved customer stories into visible social proof.'
    }
  ];
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      ...(alternative.slug === 'trustpilot' ? [
        {
          '@type': 'Article',
          headline: metadata.h1,
          description: metadata.description,
          mainEntityOfPage: metadata.canonical,
          author: { '@type': 'Organization', name: 'Clientalio' }
        },
        {
          '@type': 'ItemList',
          name: 'Trustpilot alternatives',
          itemListElement: competitors.map((competitor, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: `${competitor.name} Alternative`,
            url: `https://clientalio.com/alternatives/${competitor.slug}`
          }))
        }
      ] : []),
      {
        '@type': 'SoftwareApplication',
        name: 'Clientalio',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web'
      },
      {
        '@type': 'WebPage',
        name: metadata.h1,
        description: metadata.description,
        url: metadata.canonical
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://clientalio.com/' },
          { '@type': 'ListItem', position: 2, name: 'Alternatives', item: 'https://clientalio.com/alternatives' },
          { '@type': 'ListItem', position: 3, name: `${alternative.name} Alternative`, item: metadata.canonical }
        ]
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer }
        }))
      }
    ]
  };

  return (
    <main className="blog-page alternative-page">
      <Helmet>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        {metadata.keywords && <meta name="keywords" content={metadata.keywords} />}
        <meta name="robots" content={metadata.robots} />
        <link rel="canonical" href={metadata.canonical} />
        <meta property="og:type" content={metadata.ogType} />
        <meta property="og:title" content={metadata.ogTitle} />
        <meta property="og:description" content={metadata.ogDescription} />
        <meta property="og:url" content={metadata.canonical} />
        <meta property="og:image" content={metadata.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.twitterTitle} />
        <meta name="twitter:description" content={metadata.twitterDescription} />
        <meta name="twitter:image" content={metadata.twitterImage} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section className="blog-hero">
        <div className="container blog-hero-inner">
          <p className="blog-eyebrow">Clientalio comparison</p>
          <h1>{metadata.h1}</h1>
          <p>{metadata.description}</p>
          <div className="alternative-actions">
            <Link className="alternative-primary-action" to="/signup">Start collecting testimonials</Link>
            <Link className="alternative-secondary-action" to="/alternatives">View all alternatives</Link>
          </div>
        </div>
      </section>

      {renderLegacyContent && alternative.slug === 'trustpilot' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <h3>Why consider Clientalio?</h3>
            <h4>A way to collect and showcase customer stories</h4>
            <p>Trustpilot is built around an open review platform where customers can discover companies and share their experiences. For some businesses that's what they need. For others the goal is more focused: collect testimonials directly from customers and use the best stories on their own website and marketing channels. That's where Clientalio can fit.</p>

            <h4>Collect feedback in formats</h4>
            <p>Not every customer wants to write a long review. With Clientalio customers can share their experience through <strong>video, text or audio</strong>, giving them a way to tell their story.</p>

            <h4>Keep your testimonials in one place</h4>
            <p>Once you start collecting customer feedback managing those responses can become difficult. Clientalio gives your team a central place to review customer stories, organize submissions and keep track of the testimonials you want to use.</p>

            <h4>Put customer stories on your website</h4>
            <p>A review is most useful when potential customers can actually see it. Clientalio lets you showcase approved testimonials through <strong>website social proof widgets</strong> so you can add customer stories to landing pages, product pages and other parts of your website.</p>

            <h3>Trustpilot alternatives at a glance</h3>
            <p>There are types of Trustpilot alternatives. Some focus on public company reviews while others focus more on collecting first-party testimonials and displaying them on your website.</p>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Best suited for</th>
                    <th>Text testimonials</th>
                    <th>Video testimonials</th>
                    <th>Website widgets</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Clientalio</th>
                    <td>Customer testimonials and social proof</td>
                    <td>Included</td>
                    <td>Included</td>
                    <td>Included</td>
                  </tr>
                  <tr>
                    <th>Trustpilot</th>
                    <td>Public customer reviews</td>
                    <td>Included</td>
                    <td>Check current offering</td>
                    <td>Check current plan</td>
                  </tr>
                  <tr>
                    <th>Testimonial.to</th>
                    <td>Video and text testimonials</td>
                    <td>Included</td>
                    <td>Included</td>
                    <td>Included</td>
                  </tr>
                  <tr>
                    <th>Senja</th>
                    <td>Testimonials and social proof</td>
                    <td>Included</td>
                    <td>Included</td>
                    <td>Included</td>
                  </tr>
                  <tr>
                    <th>Famewall</th>
                    <td>Testimonials and social proof</td>
                    <td>Included</td>
                    <td>Included</td>
                    <td>Included</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p><strong>Note:</strong> Features, pricing and plans can change. Check each platform's offering before making a final decision.</p>

            <h3>Choosing a Trustpilot alternative</h3>
            <p>The best option depends on what you want to do with customer feedback.</p>

            <h4>Choose a platform based on your workflow</h4>
            <p>If your main goal is to <strong>collect reviews and help potential customers discover your business</strong> a review marketplace such as Trustpilot may make sense. If your goal is to <strong>collect customer testimonials and control how those stories appear on your website</strong> a testimonial platform such as Clientalio may be a fit.</p>

            <p>Clientalio is worth considering if:</p>
            <ul>
              <li>You want to collect <strong>text, video and audio testimonials</strong>.</li>
              <li>You want customers to have a way to share their experiences.</li>
              <li>You want to keep submissions organized in one place.</li>
              <li>You want to choose which customer stories you showcase.</li>
              <li>You want to display testimonials on your website.</li>
              <li>You want customer stories to become part of your marketing and conversion workflow.</li>
            </ul>

            <h3>Trustpilot alternatives FAQs</h3>
            <details>
              <summary>What is the Trustpilot alternative?</summary>
              <p>There isn't one alternative that works for every business. It depends on whether you need a public review platform, a testimonial collection tool or a combination of review management and website proof. Clientalio is an option for businesses that mainly want to <strong>collect, manage and showcase customer testimonials</strong> through their marketing channels.</p>
            </details>
            <details>
              <summary>Is Clientalio a replacement for Trustpilot?</summary>
              <p>Clientalio can be an alternative for businesses that don't need a public review marketplace and instead want to collect customer stories and display them on their own website. The two platforms serve different purposes so the right choice depends on your review and testimonial strategy.</p>
            </details>
            <details>
              <summary>Can Clientalio collect video testimonials?</summary>
              <p>Yes. Clientalio supports <strong>video, text and audio testimonial collection</strong> allowing customers to share their experiences in the format that works best for them.</p>
            </details>
            <details>
              <summary>Can I display Clientalio testimonials on my website?</summary>
              <p>Yes. Clientalio provides <strong>widgets</strong> that you can use to display approved customer stories as social proof across your website.</p>
            </details>
            <details>
              <summary>Why look for an alternative?</summary>
              <p>Businesses look for alternatives for different reasons. Some want control over how testimonials are collected and displayed while others want a simpler way to turn customer feedback into website content and social proof.</p>
            </details>
            <details>
              <summary>Can I use Clientalio alongside Trustpilot?</summary>
              <p>Yes. You can use platforms for different purposes. For example you might use a public review platform for reviews while using Clientalio to collect customer stories and showcase selected testimonials across your website.</p>
            </details>

            <h3>Build trust with real customer stories</h3>
            <p>Customer feedback shouldn't have to sit in one place and get forgotten. Collect the stories your customers want to share, organize your testimonials and put them where potential customers can actually see them.</p>
            <div style={{marginTop: '1rem'}}>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'vouch' && (
        <>
          <section className="alternative-section">
            <div className="container alternative-content">
              <div className="alternative-intro">
                <p className="blog-eyebrow">Why consider Clientalio?</p>
                <h2>A simpler way to collect and use customer stories</h2>
                <p><strong>Clientalio</strong> helps businesses collect video, text and audio testimonials, organize customer stories and turn them into social proof for websites, landing pages and marketing campaigns.</p>
                <p>Getting a testimonial is only the first step. Once customers reply, your team needs a practical way to review every response, choose the strongest stories and show them to prospective buyers. That is where Clientalio focuses.</p>
              </div>

              <div className="alternative-benefits">
                <article>
                  <h3>Collect without complexity</h3>
                  <p>Let customers respond with <strong>video, text or audio</strong>, so they can share their experience in the format that feels most natural.</p>
                </article>
                <article>
                  <h3>Keep stories organized</h3>
                  <p>Review every submission in one place and keep track of the testimonials your team wants to publish and reuse.</p>
                </article>
                <article>
                  <h3>Turn stories into proof</h3>
                  <p>Show approved testimonials through website-ready widgets on landing pages, product pages and other high-intent areas.</p>
                </article>
              </div>
            </div>
          </section>

          <section className="alternative-section alternative-section-muted">
            <div className="container alternative-content">
              <div className="alternative-intro">
                <p className="blog-eyebrow">Feature overview</p>
                <h2>Clientalio vs Vouch</h2>
                <p>Choose the platform that fits how your team collects and uses customer feedback. Product capabilities and plans can change, so confirm Vouch's current offering before deciding.</p>
              </div>
              <div className="alternative-table-wrap">
                <table className="alternative-table">
                  <thead>
                    <tr><th>Capability</th><th>Clientalio</th><th>Vouch</th></tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map(([feature, clientalio, competitor]) => (
                      <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="alternative-section">
            <div className="container alternative-content alternative-decision">
              <div>
                <p className="blog-eyebrow">Choosing your platform</p>
                <h2>When does Clientalio make sense?</h2>
              </div>
              <ul>
                <li>Collect <strong>text, video and audio testimonials</strong> from customers.</li>
                <li>Give customers a straightforward way to share their experience.</li>
                <li>Keep every submission organized in one place.</li>
                <li>Choose which customer stories your team showcases.</li>
                <li>Add testimonials to your website with social proof widgets.</li>
              </ul>
            </div>
          </section>

          <section className="alternative-section alternative-section-muted">
            <div className="container alternative-content">
              <div className="alternative-intro">
                <p className="blog-eyebrow">Frequently asked questions</p>
                <h2>Vouch alternative FAQs</h2>
              </div>
              <div className="alternative-faqs">
                <details>
                  <summary>What is a good Vouch alternative?</summary>
                  <p><strong>Clientalio</strong> is a Vouch alternative for businesses that want to collect, manage and showcase customer testimonials in one place. It supports text, video and audio testimonials plus social proof widgets for your website.</p>
                </details>
                <details>
                  <summary>Can Clientalio collect video testimonials?</summary>
                  <p>Yes. Customers can submit <strong>video testimonials</strong> through a simple collection process. You can also collect text and audio feedback.</p>
                </details>
                <details>
                  <summary>Can I collect text testimonials with Clientalio?</summary>
                  <p>Yes. Clientalio supports text testimonials alongside video and audio, allowing each customer to choose how they share their story.</p>
                </details>
                <details>
                  <summary>Can I display Clientalio testimonials on my website?</summary>
                  <p>Yes. Clientalio provides widgets for displaying approved customer stories as visible social proof across your website.</p>
                </details>
              </div>
            </div>
          </section>

          <section className="alternative-section">
            <div className="container alternative-content alternative-final-cta">
              <h2>Turn customer experiences into trust</h2>
              <p>Collect, organize and share the customer stories that help prospective buyers choose your business.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </section>
        </>
      )}

      {renderLegacyContent && alternative.slug === 'endorsal' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why consider Clientalio?</p>
              <h3>A focused way to turn customer feedback into proof</h3>
              <p>When comparing Clientalio and Endorsal, focus on the workflow your team needs: asking for customer stories, reviewing them, sorting approved proof and publishing it where buyers decide.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in formats</h4>
                <p>Give customers a simple way to send video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Manage proof</h4>
                <p>Keep customer stories organized so your team can review and reuse them easily.</p>
              </article>
              <article>
                <h4>Publish with confidence</h4>
                <p>Show approved testimonials using website-ready social proof widgets.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature overview</p>
              <h3>Clientalio vs Endorsal</h3>
              <p>Product capabilities and plans can change. Confirm Endorsal features directly before making a final decision.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>Endorsal</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio right for your team?</h4>
              <ul>
                <li>You want text, video and audio collection in one place.</li>
                <li>You need a testimonial request experience for customers.</li>
                <li>You want to organize and publish customer proof from one workflow.</li>
                <li>You need proof widgets that fit your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>Endorsal alternative FAQs</h3>
              <details>
                <summary>What is a good Endorsal alternative?</summary>
                <p>Clientalio is an option for teams that want to collect, manage and showcase text, video and audio customer testimonials in one workflow.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio supports video, text and audio testimonial collection through customer-friendly requests.</p>
              </details>
              <details>
                <summary>Can I publish testimonials on my website?</summary>
                <p>Yes. Clientalio provides website-ready testimonial widgets for turning approved customer stories into visible social proof.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust with authentic customer stories</h3>
              <p>Collect, manage and publish testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'vocal-video' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why consider Clientalio?</p>
              <h3>A focused way to turn customer feedback into proof</h3>
              <p>When you compare Clientalio with Vocal Video, look at the workflow your team needs: asking customers for stories, reviewing what they send, sorting approved proof and putting it out where buyers decide.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in formats</h4>
                <p>Give customers a simple way to send video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Manage proof</h4>
                <p>Keep customer stories neat so your team can review and reuse them quickly.</p>
              </article>
              <article>
                <h4>Publish with confidence</h4>
                <p>Show approved testimonials using website-ready social proof widgets.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature overview</p>
              <h3>Clientalio vs Vocal Video</h3>
              <p>Product capabilities and plans can change. Check the Vocal Video features before you decide.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>Vocal Video</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio right for your team?</h4>
              <ul>
                <li>You want text, video and audio collection together.</li>
                <li>You need a way to ask customers for testimonials.</li>
                <li>You want to organize and publish customer proof from one workflow.</li>
                <li>You need proof widgets that fit your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>Vocal Video alternative FAQs</h3>
              <details>
                <summary>What is a Vocal Video alternative?</summary>
                <p>Clientalio is a choice for teams that want to collect, manage and display text and video customer testimonials in one workflow.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio collects video, text and audio testimonials through customer-friendly requests.</p>
              </details>
              <details>
                <summary>Can I publish testimonials on my website?</summary>
                <p>Yes. Clientalio gives website-ready testimonial widgets to turn approved customer stories into visible social proof.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust with authentic customer stories</h3>
              <p>Collect, manage and publish testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'videoask' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why consider Clientalio?</p>
              <h3>A simple way to turn customer feedback into proof</h3>
              <p>When you compare Clientalio with VideoAsk, think about the work your team needs to do: ask for customer stories, look at what people send, keep the proof organized and put that proof on the pages where buyers decide to buy.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in ways</h4>
                <p>Give your customers an easy way to send video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Keep all your proof in one spot</h4>
                <p>Keep all your customer stories in one place so your team can look at and use Clientalio content quickly.</p>
              </article>
              <article>
                <h4>Post with confidence</h4>
                <p>Show your approved testimonials using website widgets that work for social proof.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature overview</p>
              <h3>Clientalio vs VideoAsk</h3>
              <p>Product features and plans can change. Please check the VideoAsk features before you make a final choice.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>VideoAsk</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio right for your team?</h4>
              <ul>
                <li>You want to collect text, video and audio in one place.</li>
                <li>You need a way to ask for testimonials that's easy for your customers.</li>
                <li>You want to organize and post customer proof using one workflow.</li>
                <li>You need proof widgets that look good on your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>VideoAsk alternative FAQs</h3>
              <details>
                <summary>What is a good VideoAsk alternative?</summary>
                <p>Clientalio is a great choice for teams that want to collect, manage and show text and video customer testimonials all in one workflow.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio lets you collect video, text and audio testimonials through requests that customers will like.</p>
              </details>
              <details>
                <summary>Can I publish testimonials on my website?</summary>
                <p>Yes. Clientalio gives you website widgets so you can turn customer stories into social proof that people can see.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust, with authentic customer stories</h3>
              <p>Collect, manage and publish testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'testimonialto' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why choose Clientalio?</p>
              <h3>A way to turn customer feedback into proof</h3>
              <p>When you are comparing Clientalio with Testimonial.to you should think about the workflow your team really needs: ask for customer stories, look at submissions, organize approved proof and then share that proof where buyers make their decisions.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in formats</h4>
                <p>Make it simple for your customers to send you video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Manage proof in one place</h4>
                <p>Keep all your customer stories in one place so your team can review and use them without any problems.</p>
              </article>
              <article>
                <h4>Publish with confidence</h4>
                <p>Show your approved testimonials using website widgets that are effective for social proof.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature details</p>
              <h3>Clientalio compared to Testimonial.to</h3>
              <p>Product features and plans can change. Please check the current Testimonial.to features yourself before you make a decision.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>Testimonial.to</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio a good fit for your team?</h4>
              <ul>
                <li>You want to keep text, video and audio collection in one place.</li>
                <li>You need a way to ask for testimonials that's simple for your customers.</li>
                <li>You want to organize and share customer proof using one process.</li>
                <li>You need proof widgets that look good on your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>Testimonial.to alternative FAQs</h3>
              <details>
                <summary>What is a good alternative to Testimonial.to?</summary>
                <p>Clientalio is a choice for teams that want to collect, manage and display text and video customer testimonials in one process.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio allows you to collect video, text and audio testimonials through requests that are easy for customers to use.</p>
              </details>
              <details>
                <summary>Can I share testimonials on my website?</summary>
                <p>Yes. Clientalio gives you website widgets so you can turn approved customer stories into social proof that people can see.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust, with customer stories</h3>
              <p>You can collect, manage and share testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'testimonial-hero' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why consider Clientalio?</p>
              <h3>A focused way to turn customer feedback into proof</h3>
              <p>When comparing Clientalio with Testimonial Hero focus on the workflow your team needs: requesting customer stories, reviewing submissions, organizing approved proof and publishing it where buyers make decisions.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in formats</h4>
                <p>Give customers a straightforward way to submit video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Manage proof</h4>
                <p>Keep customer stories organized so your team can review and reuse them efficiently.</p>
              </article>
              <article>
                <h4>Publish with confidence</h4>
                <p>Showcase approved testimonials through website-ready social proof widgets.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature overview</p>
              <h3>Clientalio vs Testimonial Hero</h3>
              <p>Product capabilities and plans can change. Confirm Testimonial Hero features directly before making a final decision.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>Testimonial Hero</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio right for your team?</h4>
              <ul>
                <li>You want text, video and audio collection in one place.</li>
                <li>You need a testimonial request experience for customers.</li>
                <li>You want to organize and publish customer proof from one workflow.</li>
                <li>You need proof widgets that fit your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>Hero alternative FAQs</h3>
              <details>
                <summary>What is a good Testimonial Hero alternative?</summary>
                <p>Clientalio is an option for teams that want to collect, manage and showcase text and video customer testimonials in one workflow.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio supports video, text and audio testimonial collection through customer-friendly requests.</p>
              </details>
              <details>
                <summary>Can I publish testimonials on my website?</summary>
                <p>Yes. Clientalio provides website-ready testimonial widgets for turning approved customer stories into visible social proof.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust with authentic customer stories</h3>
              <p>Collect, manage and publish testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'famewall' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why consider Clientalio?</p>
              <h3>A way to turn customer feedback into social proof</h3>
              <p>When you are comparing Clientalio with Famewall you should think about the workflow your team needs: request customer stories, look over submissions, organize approved proof and then publish that proof where buyers make their choices.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in ways</h4>
                <p>Give your customers an easy way to send in video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Manage proof in one spot</h4>
                <p>Keep your customer stories organized so your team can review and use those customer stories without any trouble.</p>
              </article>
              <article>
                <h4>Publish with confidence</h4>
                <p>Show your approved testimonials using website widgets that work as social proof.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature overview</p>
              <h3>Clientalio vs Famewall</h3>
              <p>Product features and plans can change. Please check the Famewall features yourself before you make a final decision.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>Famewall</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio right for your team?</h4>
              <ul>
                <li>You want to have text, video and audio collection in one place.</li>
                <li>You need a way to request testimonials that's easy for your customers.</li>
                <li>You want to organize and publish customer proof using one workflow.</li>
                <li>You need proof widgets that look good on your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>Famewall alternative FAQs</h3>
              <details>
                <summary>What is a good Famewall alternative?</summary>
                <p>Clientalio is a great option for teams that want to collect, manage and show text and video customer testimonials in one single workflow.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio supports video, text and audio testimonial collection through requests that are easy for customers to use.</p>
              </details>
              <details>
                <summary>Can I publish testimonials on my website?</summary>
                <p>Yes. Clientalio gives you website-testimonial widgets so you can turn approved customer stories into social proof that people can see.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust, with real customer stories</h3>
              <p>Collect, manage and publish testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'senja' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why consider Clientalio?</p>
              <h3>A focused way to turn customer feedback into proof</h3>
              <p>When comparing Clientalio with Senja, focus on the workflow your team needs: requesting customer stories, reviewing submissions, organizing approved proof and publishing it where buyers make decisions.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in formats</h4>
                <p>Give customers a straightforward way to submit video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Manage proof</h4>
                <p>Keep customer stories organized so your team can review and reuse them efficiently.</p>
              </article>
              <article>
                <h4>Publish with confidence</h4>
                <p>Showcase approved testimonials through website-ready social proof widgets.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature overview</p>
              <h3>Clientalio vs Senja</h3>
              <p>Product capabilities and plans can change. Confirm current Senja features directly before making a decision.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>Senja</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio right for your team?</h4>
              <ul>
                <li>You want text, video and audio collection in one place.</li>
                <li>You need a testimonial request experience for customers.</li>
                <li>You want to organize and publish customer proof from one workflow.</li>
                <li>You need proof widgets that fit your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>Senja alternative FAQs</h3>
              <details>
                <summary>What is a good Senja alternative?</summary>
                <p>Clientalio is an option for teams that want to collect, manage and showcase text and video customer testimonials in one workflow.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio supports video, text and audio testimonial collection through customer-friendly requests.</p>
              </details>
              <details>
                <summary>Can I publish testimonials on my website?</summary>
                <p>Yes. Clientalio provides website-testimonial widgets for turning approved customer stories into visible social proof.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust with authentic customer stories</h3>
              <p>Collect, manage and publish testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'bonjoro' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why consider Clientalio?</p>
              <h3>A focused way to turn customer feedback into proof</h3>
              <p>When comparing Clientalio with Bonjoro Testimonials, focus on the workflow your team needs: asking customers for stories, reviewing their answers, organizing approved proof and publishing it where buyers decide.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in formats</h4>
                <p>Give customers a clear way to submit video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Manage proof</h4>
                <p>Keep customer stories organized so your team can review and reuse them easily.</p>
              </article>
              <article>
                <h4>Publish with confidence</h4>
                <p>Show approved testimonials using website-ready social proof widgets.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature overview</p>
              <h3>Clientalio vs Bonjoro Testimonials</h3>
              <p>Product capabilities and plans can change. Confirm current Bonjoro Testimonials features directly before deciding.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>Bonjoro Testimonials</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio right for your team?</h4>
              <ul>
                <li>You want text, video and audio collection in one place.</li>
                <li>You need a testimonial request experience for customers.</li>
                <li>You want to organize and publish customer proof from one workflow.</li>
                <li>You need proof widgets that fit your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>Bonjoro Testimonials FAQs</h3>
              <details>
                <summary>What is a good Bonjoro Testimonials alternative?</summary>
                <p>Clientalio is a choice for teams that want to collect, manage and showcase text and video customer testimonials in one workflow.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio supports collecting video, text and audio testimonials through customer-friendly requests.</p>
              </details>
              <details>
                <summary>Can I publish testimonials on my website?</summary>
                <p>Yes. Clientalio provides website-testimonial widgets to display approved customer stories as visible social proof.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust, with authentic customer stories</h3>
              <p>Collect, manage and publish testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      {renderLegacyContent && alternative.slug === 'boastio' && (
        <section className="alternative-section">
          <div className="container alternative-content">
            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Why consider Clientalio?</p>
              <h3>A focused way to turn customer feedback into proof</h3>
              <p>When comparing Clientalio with Boast.io, focus on the workflow your team needs: requesting customer stories, reviewing submissions, organizing approved proof and publishing it where buyers make decisions.</p>
            </div>

            <div className="alternative-benefits">
              <article>
                <h4>Collect in formats</h4>
                <p>Give customers a straightforward way to submit video, text or audio feedback.</p>
              </article>
              <article>
                <h4>Manage proof</h4>
                <p>Keep customer stories organized so your team can review and reuse them efficiently.</p>
              </article>
              <article>
                <h4>Publish with confidence</h4>
                <p>Showcase approved testimonials through website-ready social proof widgets.</p>
              </article>
            </div>

            <div className="alternative-intro" style={{marginTop: '1rem'}}>
              <p className="blog-eyebrow">Feature overview</p>
              <h3>Clientalio vs Boast.io</h3>
              <p>Product capabilities and plans can change. Confirm Boast.io features directly before making a final decision.</p>
            </div>

            <div className="alternative-table-wrap">
              <table className="alternative-table">
                <thead>
                  <tr><th>Capability</th><th>Clientalio</th><th>Boast.io</th></tr>
                </thead>
                <tbody>
                  {comparisonRows.map(([feature, clientalio, competitor]) => (
                    <tr key={feature}><th scope="row">{feature}</th><td>{clientalio}</td><td>{competitor}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alternative-decision" style={{marginTop: '1rem'}}>
              <h3>Choosing your platform</h3>
              <h4>Is Clientalio right for your team?</h4>
              <ul>
                <li>You want text, video and audio collection in one place.</li>
                <li>You need a testimonial request experience for customers.</li>
                <li>You want to organize and publish customer proof from one workflow.</li>
                <li>You need proof widgets that fit your website.</li>
              </ul>
            </div>

            <div className="alternative-faqs" style={{marginTop: '1rem'}}>
              <h3>Boast.io alternative FAQs</h3>
              <details>
                <summary>What is a good Boast.io alternative?</summary>
                <p>Clientalio is an option for teams that want to collect, manage and showcase text and video customer testimonials in one workflow.</p>
              </details>
              <details>
                <summary>Can Clientalio collect video testimonials?</summary>
                <p>Yes. Clientalio supports video, text and audio testimonial collection through customer-friendly requests.</p>
              </details>
              <details>
                <summary>Can I publish testimonials on my website?</summary>
                <p>Yes. Clientalio provides website-testimonial widgets for turning approved customer stories into visible social proof.</p>
              </details>
            </div>

            <div style={{marginTop: '1rem'}}>
              <h3>Build trust with authentic customer stories</h3>
              <p>Collect, manage and publish testimonials with Clientalio.</p>
              <Link className="alternative-primary-action" to="/signup">Get started</Link>
            </div>
          </div>
        </section>
      )}

      <AlternativeSections alternative={alternative} faqs={faqs} />
    </main>
  );
}

export default AlternativePage;